import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { Author, Category, PostSummary } from '@/types'

import Link from 'next/link'

import {
    ArrowRight,
    ArrowUpRight,
    Layers,
    Leaf,
    Sofa
} from 'lucide-react'
import { getAuthor, getCategories, getFeaturedPost, getPosts } from '@/lib/journal'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Eyebrow } from '@/components/generals/eyebrow'
import { ContactCta } from '@/components/generals/contact-cta'
import { buttonVariants } from '@/components/ui/button'
import { PostCard } from '@/components/journal/post-card'
import { AuthorAvatar } from '@/components/journal/author-avatar'
import { FeaturedStory } from '@/components/journal/featured-story'
import { siteImages } from '@/constants/site'

interface HomeProps {
    author: Author | null
    featured: PostSummary | null
    latest: PostSummary[]
    categories: Category[]
}

const principles = [
    {
        icon: Sofa,
        title: "Live first",
        text: "A room has to work on an ordinary Tuesday before it can look good in a photograph."
    },
    {
        icon: Leaf,
        title: "Fewer, better things",
        text: "Natural materials and well-made pieces that grow more beautiful with age."
    },
    {
        icon: Layers,
        title: "Collected over time",
        text: "Homes evolve, so I always leave room for what comes next."
    }
]

export const getServerSideProps: GetServerSideProps<HomeProps> = async () => {
    try {
        const [author, featured, posts, categories] = await Promise.all([
            getAuthor(),
            getFeaturedPost(),
            getPosts({ limit: 7 }),
            getCategories()
        ])

        const latest = posts.filter(post => post.id !== featured?.id).slice(0, 6)

        return { props: { author, featured, latest, categories } }
    }
    catch (error) {
        console.error('The home page could not load the journal:', error instanceof Error ? error.message : error)

        return { props: { author: null, featured: null, latest: [], categories: [] } }
    }
}

// Defaults cover a dev hot swap that re-renders the page before getServerSideProps has supplied its props.
const Home: FC<HomeProps> = ({
    author = null,
    featured = null,
    latest = [],
    categories = []
}): ReactNode => {
    return (
        <Layout>
            <Seo />

            <section id='hero' className='relative isolate overflow-hidden'>
                <div
                    aria-hidden='true'
                    className='pointer-events-none absolute -top-40 right-[-10%] -z-10 size-[42rem] rounded-full bg-accent/70 blur-3xl'
                />

                <div className='mx-auto grid max-w-7xl items-center gap-16 px-5 pt-10 pb-20 sm:px-8 lg:grid-cols-12 lg:gap-10 lg:pt-16 lg:pb-28'>
                    <div className='animate-in fade-in slide-in-from-bottom-4 duration-700 ease-out motion-reduce:animate-none lg:col-span-6'>
                        {author?.role ? (
                            <Eyebrow>
                                <span data-mdlfy-cms={`authors:${author.id}:role`}>{author.role}</span>
                            </Eyebrow>
                        ) : (
                            <Eyebrow>
                                Home design journal
                            </Eyebrow>
                        )}

                        <h1 className='mt-7 font-heading text-[2.9rem] leading-[1.02] tracking-tight sm:text-6xl lg:text-[4.5rem]'>
                            Homes that feel like the people <em className='text-primary'>who live in them.</em>
                        </h1>

                        {author ? (
                            <p className='mt-7 max-w-xl text-lg leading-relaxed text-muted-foreground sm:text-xl'>
                                Hi, I'm <span data-mdlfy-cms={`authors:${author.id}:name`} className='text-foreground'>{author.name}</span>.{' '}
                                {author.bio && <span data-mdlfy-cms={`authors:${author.id}:bio`}>{author.bio}</span>}
                            </p>
                        ) : (
                            <p className='mt-7 max-w-xl text-lg leading-relaxed text-muted-foreground sm:text-xl'>
                                Notes on warm, lived-in homes and the small decisions that make a house feel like yours.
                            </p>
                        )}

                        <div className='mt-10 flex flex-wrap items-center gap-3'>
                            <Link href='/journal' className={buttonVariants({ size: 'lg', className: 'group' })}>
                                Read the journal
                                <ArrowRight className='transition-transform group-hover:translate-x-0.5' />
                            </Link>

                            <Link href='/about' className={buttonVariants({ variant: 'outline', size: 'lg' })}>
                                About me
                            </Link>
                        </div>
                    </div>

                    <div className='animate-in fade-in duration-1000 ease-out motion-reduce:animate-none lg:col-span-6 lg:pl-10 xl:pl-16'>
                        {featured ? (
                            <FeaturedStory post={featured} />
                        ) : (
                            <div className='relative mx-auto max-w-md'>
                                <img
                                    src={siteImages.portrait.src}
                                    alt={siteImages.portrait.alt}
                                    fetchPriority='high'
                                    className='aspect-[4/5] w-full rounded-t-full rounded-b-3xl object-cover'
                                />
                            </div>
                        )}
                    </div>
                </div>
            </section>

            <section id='latest' className='mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-28'>
                <div className='flex flex-col gap-6 border-b border-border pb-8 sm:flex-row sm:items-end sm:justify-between'>
                    <div>
                        <Eyebrow>From the journal</Eyebrow>

                        <h2 className='mt-5 font-heading text-4xl tracking-tight sm:text-5xl'>
                            Latest stories
                        </h2>
                    </div>

                    <Link href='/journal' className='group inline-flex items-center gap-2 self-start font-medium sm:self-auto'>
                        View all stories
                        <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' />
                    </Link>
                </div>

                {latest.length > 0 ? (
                    <div className='mt-12 grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3'>
                        {latest.map(post => (
                            <PostCard key={post.id} post={post} />
                        ))}
                    </div>
                ) : (
                    <p className='mt-12 text-lg text-muted-foreground'>
                        New stories are on the way. Check back soon.
                    </p>
                )}
            </section>

            {categories.length > 0 && (
                <section id='topics' className='bg-secondary/60'>
                    <div className='mx-auto grid max-w-7xl gap-12 px-5 py-20 sm:px-8 lg:grid-cols-12 lg:py-28'>
                        <div className='lg:col-span-4'>
                            <Eyebrow>Explore by topic</Eyebrow>

                            <h2 className='mt-5 font-heading text-4xl tracking-tight sm:text-5xl'>
                                Find your room
                            </h2>

                            <p className='mt-5 max-w-sm leading-relaxed text-muted-foreground'>
                                Every story is filed by the space it helps with, so you can go straight to the room you're working on.
                            </p>
                        </div>

                        <ul className='divide-y divide-border border-y border-border lg:col-span-8'>
                            {categories.map((category, index) => (
                                <li key={category.id}>
                                    <Link
                                        href={`/journal/category/${category.slug}`}
                                        className='group grid grid-cols-[auto_1fr_auto] items-baseline gap-x-5 py-7 sm:gap-x-8'
                                    >
                                        <span className='font-heading text-sm text-muted-foreground tabular-nums'>
                                            {String(index + 1).padStart(2, '0')}
                                        </span>

                                        <span>
                                            <span
                                                data-mdlfy-cms={`categories:${category.id}:name`}
                                                className='block font-heading text-2xl tracking-tight transition-colors group-hover:text-primary sm:text-3xl'
                                            >
                                                {category.name}
                                            </span>

                                            {category.description && (
                                                <span
                                                    data-mdlfy-cms={`categories:${category.id}:description`}
                                                    className='mt-2 block leading-relaxed text-muted-foreground'
                                                >
                                                    {category.description}
                                                </span>
                                            )}
                                        </span>

                                        <span className='flex items-center gap-4 self-center text-sm text-muted-foreground'>
                                            <span className='hidden whitespace-nowrap sm:inline'>
                                                {category.post_count} {category.post_count === 1 ? "story" : "stories"}
                                            </span>

                                            <ArrowUpRight className='size-5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary' />
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            )}

            <section id='approach' className='mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-28'>
                <div className='grid items-center gap-14 lg:grid-cols-12 lg:gap-10'>
                    <div className='lg:col-span-5'>
                        <div className='relative mx-auto max-w-sm lg:max-w-none'>
                            <div
                                aria-hidden='true'
                                className='absolute -top-5 -left-5 right-10 bottom-16 rounded-t-full border border-primary/30'
                            />

                            {author?.avatar ? (
                                <img
                                    src={author.avatar}
                                    alt={author.avatar_alt ?? ''}
                                    loading='lazy'
                                    decoding='async'
                                    className='relative aspect-[4/5] w-full rounded-t-full rounded-b-3xl object-cover'
                                />
                            ) : (
                                <img
                                    src={siteImages.portrait.src}
                                    alt={siteImages.portrait.alt}
                                    loading='lazy'
                                    decoding='async'
                                    className='relative aspect-[4/5] w-full rounded-t-full rounded-b-3xl object-cover'
                                />
                            )}
                        </div>
                    </div>

                    <div className='lg:col-span-7 lg:pl-12'>
                        <Eyebrow>My approach</Eyebrow>

                        <blockquote className='mt-7 font-heading text-3xl leading-snug tracking-tight sm:text-4xl'>
                            &ldquo;The best rooms aren't finished in a weekend. They're collected slowly, edited often and lived in every day.&rdquo;
                        </blockquote>

                        <ul className='mt-12 grid gap-8 sm:grid-cols-3'>
                            {principles.map(principle => (
                                <li key={principle.title}>
                                    <span className='inline-flex size-11 items-center justify-center rounded-full bg-accent text-accent-foreground'>
                                        <principle.icon aria-hidden='true' className='size-5' />
                                    </span>

                                    <h3 className='mt-4 font-heading text-xl'>
                                        {principle.title}
                                    </h3>

                                    <p className='mt-2 leading-relaxed text-muted-foreground'>
                                        {principle.text}
                                    </p>
                                </li>
                            ))}
                        </ul>

                        {author && (
                            <div className='mt-12 flex flex-wrap items-center gap-x-6 gap-y-4 border-t border-border pt-8'>
                                <div className='flex items-center gap-4'>
                                    <AuthorAvatar
                                        name={author.name}
                                        avatar={author.avatar}
                                        avatarAlt={author.avatar_alt}
                                        className='size-12 text-base'
                                    />

                                    <div>
                                        <p data-mdlfy-cms={`authors:${author.id}:name`} className='font-heading text-lg leading-tight'>
                                            {author.name}
                                        </p>

                                        {author.role && (
                                            <p data-mdlfy-cms={`authors:${author.id}:role`} className='text-sm text-muted-foreground'>
                                                {author.role}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <Link href='/about' className='group inline-flex items-center gap-2 font-medium text-primary sm:ml-auto'>
                                    More about me
                                    <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' />
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            <ContactCta />
        </Layout>
    )
}

export default Home
